/**
 * D15 -- Mobile Home one-tap punch FAB.
 *
 * Persistent thumb-reach button lifted above the bottom nav on mobile
 * Home only. Shows "PUNCH IN" (blue gradient) when no active punch,
 * "PUNCH OUT" (amber gradient) when there is one. Uses
 * `.ai-fab-mobile-lift` so the bottom offset auto-clears the mobile
 * bottom nav + safe-area inset.
 *
 * Guardrails
 *   * Mobile only (`useIsMobile`) -- desktop is untouched (`hidden lg:hidden`).
 *   * Hidden when there is no employee record (CEO w/ no employee_code
 *     never sees it, staying consistent with the desktop `<PunchCard>`).
 *   * Fires the SAME `/attendance/punch-in` + `/attendance/punch-out`
 *     API contract as `<PunchCard>` -- no server-side change needed.
 *   * Uses the browser geolocation API. If location denied we surface
 *     a friendly toast instead of crashing.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut, Home } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { useIsCEO } from "@/hooks/useIsCEO";
import { useIsMobile } from "@/lib/viewport";

async function getPos() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation unavailable"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      (e) => reject(e),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 },
    );
  });
}

export default function MobileHomePunchFAB() {
  const { user } = useAuth();
  const isCEO = useIsCEO();
  const isMobile = useIsMobile();
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  // D23 -- WFH-aware FAB. Same rendering rule as PunchCard: when the
  // current user is on an approved WFH today, we swap the label to
  // "PUNCH IN (WFH)" and use the flat sky-500 fill (not the default
  // navy->sky gradient).
  const [myWfh, setMyWfh] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setToday(data);
    } catch (_e) { /* silent -- FAB is non-critical */ }
    try {
      const { data } = await api.get("/wfh/today", { silent: true });
      const mine = (data?.rows || []).find((r) => r.user_id === user?.id);
      setMyWfh(mine || null);
    } catch (_e) { setMyWfh(null); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  if (!isMobile) return null;
  if (!user?.employee_code) return null;

  const punches = today?.attendance?.punches || [];
  const last = punches[punches.length - 1];
  const isPunchedIn = Boolean(last && !last.out_time);
  const kind = isPunchedIn ? "punch-out" : "punch-in";
  const wfhLabel = myWfh && !isPunchedIn;
  const label = isPunchedIn
    ? "PUNCH OUT"
    : (wfhLabel ? "PUNCH IN (WFH)" : "PUNCH IN");
  const Icon = isPunchedIn ? LogOut : (wfhLabel ? Home : LogIn);
  // D23 -- when on WFH + off-clock we use a flat sky-500 fill so the
  // WFH punch is visually distinct from a regular punch-in.
  const gradient = isPunchedIn
    ? "linear-gradient(135deg, #c2410c 0%, #f59e0b 100%)"
    : (wfhLabel
        ? "#1E96FC"
        : "linear-gradient(135deg, #003771 0%, #1E96FC 100%)");

  const handleTap = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let body;
      if (myWfh) {
        // WFH days: geofence disabled server-side; send whatever
        // position we can get, or zeros if the browser refuses.
        try {
          const pos = await getPos();
          body = { lat: pos.lat, lng: pos.lng, accuracy: pos.acc, wfh: true };
        } catch (_geo) {
          body = { lat: 0, lng: 0, accuracy: 0, wfh: true };
        }
      } else {
        const pos = await getPos();
        body = { lat: pos.lat, lng: pos.lng, accuracy: pos.acc };
      }
      const { data } = await api.post(`/attendance/${kind}`, body);
      setToday((s) => ({ ...s, attendance: data.attendance }));
      const suffix = myWfh ? " (WFH)" : "";
      toast.success(isPunchedIn ? `Punched out${suffix}` : `Punched in${suffix}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Punch failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // D36.c -- CEO does not track attendance. Hide the mobile FAB entirely.
  if (isCEO) return null;

  return (
    <button
      type="button"
      onClick={handleTap}
      disabled={busy}
      className="ai-fab-mobile-lift fixed left-1/2 -translate-x-1/2 z-40
                    inline-flex items-center gap-2 rounded-full
                    px-6 h-12 shadow-lg shadow-sky-500/40
                    border border-white/15 text-white font-heading
                    tracking-[0.2em] text-xs uppercase
                    disabled:opacity-70 disabled:cursor-progress
                    transition-transform hover:scale-[1.03] active:scale-95
                    lg:hidden"
      style={{ background: gradient }}
      data-testid="mobile-home-punch-fab"
      data-mode={kind}
      aria-label={label}
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
      <span data-testid="mobile-home-punch-fab-label">{label}</span>
    </button>
  );
}
